import { useNavigate } from "react-router-dom";

import '../css/style.css';
import logo from '../images/minda-logo.png';
import { useState, useEffect } from "react";
import { resetPwd, sendOtp, sendInternationalOtpRstPwd } from "../api";

function ForgotPassword() {
    const navigate = useNavigate();

    // --- Flow state ---
    // Step 1: null (not answered) | 'india' | 'international'
    const [region, setRegion] = useState(null);
    // Step 2: whether OTP was sent successfully (reveals OTP + password fields)
    const [otpSent, setOtpSent] = useState(false);
    const [sendingOtp, setSendingOtp] = useState(false);

    // --- Form field state ---
    const [userId, setUserId] = useState('');
    const [otp, setOtp] = useState('');
    const [newPwd, setNewPwd] = useState('');
    const [confirmPwd, setConfirmPwd] = useState('');

    useEffect(() => {
        let token = sessionStorage.getItem('token');
        if (token) {
            navigate('../home', { replace: true });
        }
    }, []);

    // ----------------------------------------------------------------
    // Helpers
    // ----------------------------------------------------------------

    const isEmail = (value) => {
        return /\S+@\S+\.\S+/.test(value);
    };

    const normalizeIndianMobile = (value) => {
        const digits = String(value || '').replace(/\D/g, '');
        if (/^(?:91)?[6-9]\d{9}$/.test(digits)) {
            return digits.length === 12 ? digits.slice(2) : digits;
        }
        return null;
    };

    /**
     * Derives a structured `contact` object from the current userId input
     * and the selected region.
     *
     * Returns:
     *   { mobile }              – Indian mobile (10-digit)
     *   { email }               – international email
     *   { whatsapp, countryCode } – international WhatsApp
     *   null                    – invalid / unrecognised input
     */
    const deriveContact = () => {
        if (region === 'india') {
            const mobile = normalizeIndianMobile(userId);
            return mobile ? { mobile } : null;
        }

        if (region === 'international') {
            if (isEmail(userId)) {
                return { email: userId.trim() };
            }
            return { whatsapp: userId.trim() };
        }

        return null;
    };

    // ----------------------------------------------------------------
    // Actions
    // ----------------------------------------------------------------

    const handleRegionSelect = (choice) => {
        setRegion(choice);
        setUserId('');
        setOtpSent(false);
        setOtp('');
        setNewPwd('');
        setConfirmPwd('');
    };

    const sendOtpCall = async () => {
        if (sendingOtp) return;

        const contact = deriveContact();

        if (!contact) {
            if (region === 'india') {
                alert('Enter a valid Indian mobile number (e.g. 9876543210).');
            } else {
                alert('Enter a valid international email address or WhatsApp number with country code (e.g. +14155550123).');
            }
            return;
        }

        setSendingOtp(true);

        try {
            const resp = contact.mobile
                ? await sendOtp(contact.mobile)
                : await sendInternationalOtpRstPwd(contact);

                if (resp && resp.success) {
                    alert('OTP sent successfully');
                    setOtpSent(true);
                } else {
                    console.log(resp);
                    alert(resp?.message || 'Failed to send OTP');
                }
        } catch (err) {
            console.error(err);
            alert('Failed to send OTP');
        } finally {
            setSendingOtp(false);
        }
    };

    const submitResetPassword = () => {
        const contact = deriveContact();

        if (!contact) {
            alert('Enter a valid contact before submitting.');
            return;
        }
        if (!otp) {
            alert('OTP is required');
            return;
        }
        if (!newPwd) {
            alert('New Password is required');
            return;
        }
        if (!confirmPwd) {
            alert('Confirm Password is required');
            return;
        }
        if (newPwd !== confirmPwd) {
            alert('Password and Confirm Password should be the same');
            return;
        }

        resetPwd(contact, otp, newPwd, confirmPwd)
            .then(resp => {
                if (resp && resp.success) {
                    alert('Password reset successfully');
                    navigate('/login', { replace: true });
                } else {
                    alert('Failed to reset password');
                }
            })
            .catch(() => alert('Failed to reset password'));
    };

    // ----------------------------------------------------------------
    // Render helpers – keep original class names / markup intact
    // ----------------------------------------------------------------

    const renderRegionStep = () => (
        <div class="form-group">
            <p class="text-slate-500 dark:text-zink-200" style={{ marginBottom: '12px' }}>
                Are you outside India?
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
                <button
                    type="button"
                    className={`btn ${region === 'international' ? 'btn-primary' : 'btn-secondary'} !border !border-gray-200`}
                    onClick={() => handleRegionSelect('international')}
                >
                    Yes
                </button>
                <button
                    type="button"
                    class={`btn ${region === 'india' ? 'btn-primary' : 'btn-secondary'} !border !border-gray-200`}
                    onClick={() => handleRegionSelect('india')}
                >
                    No
                </button>
            </div>
        </div>
    );

    const renderContactInput = () => {
        if (!region) return null;

        const placeholder =
            region === 'india'
                ? 'Enter Mobile Number'
                : 'Enter Email or WhatsApp (e.g. +14155550123)';

        return (
            <div class="form-group mobile">
                <input
                    type="text"
                    placeholder={placeholder}
                    tabIndex="0"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    className="!mt-4"
                />
                <button
                    type="button"
                    class="btn btn-secondary !border !border-gray-200 !mt-4"
                    disabled={sendingOtp}
                    onClick={sendOtpCall}
                >
                    {sendingOtp ? (
                        <>
                            Please wait…
                        </>
                    ) : (
                        'Send OTP'
                    )}

                </button>
            </div>
        );
    };

    const renderPostOtpFields = () => {
        if (!otpSent) return null;

        return (
            <>
                <div class="form-group otp">
                    <input
                        type="text"
                        placeholder="Enter OTP"
                        tabIndex="1"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                    />
                </div>
                <div class="form-group new-password">
                    <input
                        type="password"
                        placeholder="Enter New Password"
                        tabIndex="2"
                        value={newPwd}
                        onChange={(e) => setNewPwd(e.target.value)}
                    />
                </div>
                <div class="form-group confirm-password">
                    <input
                        type="password"
                        placeholder="Confirm New Password"
                        tabIndex="3"
                        value={confirmPwd}
                        onChange={(e) => setConfirmPwd(e.target.value)}
                    />
                </div>
                <div class="form-group mt-20">
                    <button
                        type="button"
                        class="btn btn-primary btn-block"
                        onClick={submitResetPassword}
                    >
                        Submit
                    </button>
                </div>
            </>
        );
    };

    // ----------------------------------------------------------------
    // Main render – original outer structure preserved exactly
    // ----------------------------------------------------------------

    return (
        <div class="main-page">
            <div class="login-main">

                <div class="logo">
                    <img src={logo} />
                </div>

                <div class="login-wrapper">
                    <div class="login-form">
                        <diV class="title text-center">
                            <h1>Reset Your Password</h1>
                            <p class="text-slate-500 dark:text-zink-200">
                                Enter your details to reset your password
                            </p>
                        </diV>

                        <div class="form">
                            {/* Step 1 – region selector */}
                            {renderRegionStep()}

                            {/* Step 2 – contact input + Send OTP */}
                            {renderContactInput()}

                            {/* Step 3 – OTP + password fields (revealed after OTP sent) */}
                            {renderPostOtpFields()}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}

export default ForgotPassword;
